/**
 * FormBrowseModal Component
 * Modal for browsing and selecting one or more forms from the API.
 * Supports multi-select — selected forms are added as child forms.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { FormDefinitionApiClient, getAtlasApiConfig } from '@atlas-forms/client-js';

export interface FormBrowseItem {
  id: string | number;
  name: string;
  formCode?: string;
}

export interface FormBrowseModalProps {
  open: boolean;
  onClose: () => void;
  /** Called with all selected forms when user confirms */
  onSelect: (forms: FormBrowseItem[]) => void;
  /** IDs of forms already added as children — shown as disabled in the list */
  existingFormIds?: (string | number)[];
  /** Optional external form loader */
  loadForms?: () => Promise<FormBrowseItem[]>;
}

/** Case-insensitive property getter */
function prop(obj: any, ...keys: string[]): any {
  for (const k of keys) {
    if (obj[k] !== undefined) return obj[k];
  }
  return undefined;
}

/** Extract data array from BizFirst API envelope (camelCase or PascalCase) */
function extractData(body: any): any[] {
  if (Array.isArray(body)) return body;
  if (!body || typeof body !== 'object') return [];
  const data = prop(body, 'Data', 'data');
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') {
    const inner = prop(data, 'Data', 'data');
    if (Array.isArray(inner)) return inner;
  }
  return [];
}

/** Default loader — calls form list API directly via fetch with pagination */
const defaultLoadForms = async (pageNumber: number = 1, pageSize: number = 10, searchTerm?: string): Promise<{ forms: FormBrowseItem[]; total: number; page: number; pageSize: number }> => {
  // Uses the shared API client so auth and tenant headers are sent, as for every other form list call.
  const client = new FormDefinitionApiClient({ baseUrl: getAtlasApiConfig().baseUrl });
  const result = await client.listForms({
    IsEnabled: true,
    SearchTitle: searchTerm && searchTerm.trim() ? searchTerm : undefined,
    PageInfo: { PageNumber: pageNumber, PageSize: pageSize },
  });
  if (!result.success || !result.data) {
    throw new Error(`Failed to load forms: ${result.errorMessage ?? 'unknown error'}`);
  }
  const json: unknown = result.data;
  console.log('[FormBrowseModal] API Response:', json);
  const rows = extractData(json);

  // Extract total records from PageInfo (try both camelCase and PascalCase)
  let totalRecords = 0;
  const pageInfo = prop(json, 'PageInfo', 'pageInfo');
  console.log('[FormBrowseModal] PageInfo:', pageInfo);
  if (pageInfo && typeof pageInfo === 'object') {
    totalRecords = prop(pageInfo, 'TotalRecords', 'totalRecords') || 0;
  }

  // If no total from API, log a warning and use array length (may be incomplete)
  if (totalRecords === 0) {
    console.warn('[FormBrowseModal] API did not return PageInfo.TotalRecords. Using current page size as fallback.');
    totalRecords = rows.length;
  }

  console.log(`[FormBrowseModal] Page ${pageNumber}: ${rows.length} forms received, ${totalRecords} total records`);
  console.log(`[FormBrowseModal] Total Pages: ${Math.ceil(totalRecords / pageSize)}`);


  const forms = rows.map((f: any) => ({
    id: prop(f, 'FormID', 'formID', 'Id', 'id'),
    name: prop(f, 'Name', 'name', 'FormName', 'formName') ?? 'Unnamed',
    formCode: prop(f, 'FormCode', 'formCode') ?? '',
  }));

  return {
    forms,
    total: totalRecords,
    page: pageNumber,
    pageSize
  };
};

export const FormBrowseModal: React.FC<FormBrowseModalProps> = ({
  open,
  onClose,
  onSelect,
  existingFormIds = [],
  loadForms,
}) => {
  const [forms, setForms] = useState<FormBrowseItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [searchDebounceTimer, setSearchDebounceTimer] = useState<NodeJS.Timeout | null>(null);
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [totalForms, setTotalForms] = useState(0);
  const [pageSize] = useState(10);
  const overlayRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const totalPages = Math.ceil(totalForms / pageSize);

  // Wrapper for custom or default loader
  const loadFormsBatch = useCallback(async (pageNum: number, searchTerm?: string) => {
    if (loadForms) {
      // Custom loader — call it and expect array back
      const data = await loadForms();
      // Simulate pagination for custom loaders
      const filtered = searchTerm ? data.filter(f =>
        f.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(f.id).includes(searchTerm) ||
        (f.formCode && f.formCode.toLowerCase().includes(searchTerm.toLowerCase()))
      ) : data;
      const start = (pageNum - 1) * pageSize;
      const paginated = filtered.slice(start, start + pageSize);
      return { forms: paginated, total: filtered.length, page: pageNum, pageSize };
    }
    // Default loader with pagination and search
    return (defaultLoadForms as any)(pageNum, pageSize, searchTerm);
  }, [loadForms, pageSize]);

  const existingSet = useMemo(() => new Set(existingFormIds.map(String)), [existingFormIds]);

  // Load forms when modal opens
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSearch('');
    setSelected(new Set());
    setCurrentPage(1);
    setForms([]);
    setTotalForms(0);

    loadFormsBatch(1, '').then((result) => {
      if (!cancelled) {
        setForms(result.forms);
        setTotalForms(result.total);
        setLoading(false);
      }
    }).catch((err) => {
      if (!cancelled) { setError(err?.message || 'Failed to load forms'); setLoading(false); }
    });
    return () => { cancelled = true; };
  }, [open, loadFormsBatch]);

  // Handle search with debounce
  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setCurrentPage(1); // Reset to first page on search

    // Clear existing timer
    if (searchDebounceTimer) clearTimeout(searchDebounceTimer);

    // Set new timer for debounced search
    const timer = setTimeout(() => {
      setLoading(true);
      setError(null);
      loadFormsBatch(1, value).then((result) => {
        setForms(result.forms);
        setTotalForms(result.total);
        setLoading(false);
      }).catch((err) => {
        setError(err?.message || 'Failed to search forms');
        setLoading(false);
      });
    }, 300); // 300ms debounce

    setSearchDebounceTimer(timer);
  }, [loadFormsBatch, searchDebounceTimer]);

  // Handle page change
  const handlePageChange = useCallback((newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return;
    setCurrentPage(newPage);
    setLoading(true);
    setError(null);

    loadFormsBatch(newPage, search).then((result) => {
      setForms(result.forms);
      setTotalForms(result.total);
      setLoading(false);
      // Scroll to top of form list
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = 0;
      }
    }).catch((err) => {
      setError(err?.message || 'Failed to load page');
      setLoading(false);
    });
  }, [totalPages, loadFormsBatch, search]);

  // Auto-focus search
  useEffect(() => {
    if (open && !loading && searchRef.current) {
      setTimeout(() => searchRef.current?.focus(), 50);
    }
  }, [open, loading]);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    if (e.target === overlayRef.current) onClose();
  }, [onClose]);

  const toggleSelect = (id: string | number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleConfirm = () => {
    const selectedForms = forms.filter((f) => selected.has(f.id));
    if (selectedForms.length > 0) onSelect(selectedForms);
    onClose();
  };

  // Search is now handled by the API, so we don't need client-side filtering
  // The forms array already contains only the search results for the current page

  if (!open) return null;

  // Detect dark mode from document or parent
  const isDark = typeof document !== 'undefined' && (document.documentElement.className.includes('dark') || document.body.className.includes('dark'));

  // Theme colors
  const colors = {
    bg: isDark ? '#0f172a' : '#ffffff',
    border: isDark ? 'rgba(51,65,85,0.2)' : '#e5e7eb',
    text: isDark ? '#e2e8f0' : '#1f2937',
    textSecondary: isDark ? '#94a3b8' : '#6b7280',
    hover: isDark ? 'rgba(34,197,94,0.1)' : '#f3f4f6',
    selected: isDark ? 'rgba(34,197,94,0.15)' : 'rgba(34,197,94,0.08)',
    disabled: isDark ? '#1e293b' : '#f9fafb',
    headerBg: isDark ? 'rgba(15,23,42,0.5)' : '#f9fafb',
    accent: '#22c55e',
    accentDark: '#16a34a',
  };

  return (
    <div ref={overlayRef} onClick={handleOverlayClick}
      style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: colors.bg, borderRadius: 8, width: 600, maxHeight: '75vh',
        display: 'flex', flexDirection: 'column', boxShadow: isDark ? '0 20px 25px rgba(0,0,0,0.5)' : '0 8px 32px rgba(0,0,0,0.15)',
        border: `1px solid ${colors.border}` }}>

        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${colors.border}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: colors.headerBg }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: colors.text }}>
            Browse Forms {selected.size > 0 && <span style={{ fontWeight: 400, fontSize: 13, color: colors.accent }}>({selected.size} selected)</span>}
          </h3>
          <button onClick={onClose} type="button"
            style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: colors.textSecondary, padding: '0 4px', transition: 'color 0.2s' }}
            onMouseEnter={(e) => e.currentTarget.style.color = colors.text}
            onMouseLeave={(e) => e.currentTarget.style.color = colors.textSecondary}>&times;</button>
        </div>

        {/* Search */}
        <div style={{ padding: '12px 20px', borderBottom: `1px solid ${colors.border}`, background: colors.headerBg }}>
          <input ref={searchRef} type="text" placeholder="Search by name, ID, or code..."
            value={search} onChange={(e) => handleSearchChange(e.target.value)}
            style={{ width: '100%', padding: '8px 12px', fontSize: 13, border: `1px solid ${colors.border}`,
              borderRadius: 4, outline: 'none', boxSizing: 'border-box', background: colors.bg, color: colors.text,
              transition: 'border-color 0.2s' }}
            onFocus={(e) => e.currentTarget.style.borderColor = colors.accent}
            onBlur={(e) => e.currentTarget.style.borderColor = colors.border} />
        </div>

        {/* Body */}
        <div ref={scrollContainerRef} style={{ flex: 1, overflowY: 'auto', minHeight: 300, background: colors.bg, display: 'flex', flexDirection: 'column' }}>
          {loading && <div style={{ padding: 32, textAlign: 'center', color: colors.textSecondary, fontSize: 13 }}>Loading forms...</div>}
          {error && <div style={{ padding: 32, textAlign: 'center', color: '#ef4444', fontSize: 13 }}>{error}</div>}
          {!loading && !error && forms.length === 0 && (
            <div style={{ padding: 32, textAlign: 'center', color: colors.textSecondary, fontSize: 13 }}>
              {totalForms === 0 ? 'No forms available' : `No forms match "${search}"`}
            </div>
          )}
          {!loading && !error && forms.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: colors.headerBg, position: 'sticky', top: 0, borderBottom: `1px solid ${colors.border}` }}>
                  <th style={{ padding: '10px 12px', width: 36 }}></th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: colors.textSecondary, width: 70 }}>ID</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: colors.textSecondary }}>Name</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: colors.textSecondary, width: 100 }}>Code</th>
                </tr>
              </thead>
              <tbody>
                {forms.map((form) => {
                  const isExisting = existingSet.has(String(form.id));
                  const isChecked = selected.has(form.id);
                  return (
                    <tr key={form.id}
                      onClick={() => !isExisting && toggleSelect(form.id)}
                      style={{ cursor: isExisting ? 'default' : 'pointer', borderBottom: `1px solid ${colors.border}`,
                        background: isChecked ? colors.selected : isExisting ? colors.disabled : undefined,
                        opacity: isExisting ? 0.6 : 1,
                        transition: 'background 0.15s' }}
                      onMouseEnter={(e) => { if (!isExisting && !isChecked) e.currentTarget.style.background = colors.hover; }}
                      onMouseLeave={(e) => { if (!isExisting && !isChecked) e.currentTarget.style.background = ''; }}>
                      <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                        <input type="checkbox" checked={isChecked || isExisting} disabled={isExisting}
                          onChange={() => !isExisting && toggleSelect(form.id)}
                          onClick={(e) => e.stopPropagation()}
                          style={{ width: 15, height: 15, cursor: isExisting ? 'not-allowed' : 'pointer', accentColor: colors.accent }} />
                      </td>
                      <td style={{ padding: '8px 12px', color: colors.textSecondary }}>{form.id}</td>
                      <td style={{ padding: '8px 12px', fontWeight: 500, color: colors.text }}>
                        {form.name}
                        {isExisting && <span style={{ fontSize: 11, color: colors.textSecondary, marginLeft: 6 }}>(already added)</span>}
                      </td>
                      <td style={{ padding: '8px 12px', color: colors.textSecondary, fontSize: 12 }}>{form.formCode || '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination Controls */}
        {!loading && !error && forms.length > 0 && totalForms > 0 && (
          <div style={{ padding: '12px 20px', borderTop: `1px solid ${colors.border}`,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: colors.headerBg }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} type="button"
                style={{ padding: '6px 10px', fontSize: 12, borderRadius: 4, border: `1px solid ${colors.border}`,
                  background: colors.bg, color: colors.text, cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s', opacity: currentPage === 1 ? 0.5 : 1 }}
                onMouseEnter={(e) => { if (currentPage > 1) e.currentTarget.style.background = colors.hover; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = colors.bg; }}>
                ← Previous
              </button>
              <span style={{ fontSize: 12, color: colors.textSecondary, whiteSpace: 'nowrap' }}>
                Page {currentPage} of {totalPages} ({totalForms} total)
              </span>
              <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} type="button"
                style={{ padding: '6px 10px', fontSize: 12, borderRadius: 4, border: `1px solid ${colors.border}`,
                  background: colors.bg, color: colors.text, cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s', opacity: currentPage === totalPages ? 0.5 : 1 }}
                onMouseEnter={(e) => { if (currentPage < totalPages) e.currentTarget.style.background = colors.hover; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = colors.bg; }}>
                Next →
              </button>
            </div>
            <span style={{ fontSize: 12, color: colors.textSecondary }}>
              {forms.length} forms on this page
            </span>
          </div>
        )}

        {/* Footer */}
        <div style={{ padding: '12px 20px', borderTop: `1px solid ${colors.border}`,
          display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, background: colors.headerBg }}>
          <button onClick={onClose} type="button"
            style={{ padding: '6px 16px', fontSize: 13, borderRadius: 4, border: `1px solid ${colors.border}`,
              background: colors.bg, color: colors.text, cursor: 'pointer', transition: 'all 0.2s' }}
            onMouseEnter={(e) => e.currentTarget.style.background = colors.hover}
            onMouseLeave={(e) => e.currentTarget.style.background = colors.bg}>
            Cancel
          </button>
          <button onClick={handleConfirm} type="button" disabled={selected.size === 0}
            style={{ padding: '6px 16px', fontSize: 13, borderRadius: 4, border: 'none',
              background: selected.size > 0 ? colors.accent : colors.textSecondary, color: '#fff',
              cursor: selected.size > 0 ? 'pointer' : 'not-allowed', fontWeight: 600, transition: 'all 0.2s' }}
            onMouseEnter={(e) => { if (selected.size > 0) e.currentTarget.style.background = colors.accentDark; }}
            onMouseLeave={(e) => { if (selected.size > 0) e.currentTarget.style.background = colors.accent; }}>
            Add {selected.size > 0 ? `${selected.size} Form${selected.size > 1 ? 's' : ''}` : 'Forms'}
          </button>
        </div>
      </div>
    </div>
  );
};

FormBrowseModal.displayName = 'FormBrowseModal';
